import { useAppStore } from '@/store/useAppStore'
import { cn, PRIORITIES, RECURRENCE_OPTIONS, recurrenceKey, recurrenceLabel } from '@/lib/utils'
import { Avatar } from '@/components/ui/Avatar'
import { DatePicker } from '@/components/ui/DatePicker'
import { Dropdown, DropdownItem, DropdownSeparator } from '@/components/ui/Dropdown'
import {
  X, Check, User, Flag, AlignLeft, Paperclip, ChevronRight,
  Plus, Tag, Send, Trash2, MoreHorizontal, Link2, Calendar, Copy, Download,
  ArrowUpRight, RotateCcw, Clock, Timer, Loader2, FileText, Code, Maximize2,
  ChevronLeft, ChevronDown, Crop, Upload, Pencil, Play, Pause
} from 'lucide-react'
import { TaskCheckbox } from '@/components/ui/TaskCheckbox'
import { PdfThumbnail } from '@/components/ui/PdfThumbnail'
import { useContextMenu } from '@/components/ui/ContextMenu'
import { useLongPress } from '@/hooks/useLongPress'
import { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useToast } from '@/components/ui/Toast'
import AttachmentMedia from '@/components/ui/AttachmentMedia'
import { generateImageThumb, generateVideoThumb } from '@/lib/mediaThumbnails'

async function downloadFile(url, name) {
  try {
    const res = await fetch(url)
    const blob = await res.blob()
    const blobUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = name || 'download'
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)
  } catch {
    window.open(url, '_blank')
  }
}

function DetailRow({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3 py-2 group">
      <div className="flex items-center gap-2 w-28 flex-shrink-0 mt-0.5">
        <Icon size={13} className="text-text-muted flex-shrink-0" />
        <span className="text-xs text-text-muted">{label}</span>
      </div>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  )
}

function AssigneeSelector({ task }) {
  const { updateTask, getProjectMembers, currentUser, _membersCache } = useAppStore()
  const cachedMembers = _membersCache[task.projectId] || []
  const members = cachedMembers.length > 0 ? cachedMembers
    : currentUser ? [{ userId: currentUser.id, name: currentUser.name || 'Me', email: currentUser.email || '', avatar: currentUser.avatar || null, role: 'owner' }]
    : []
  useEffect(() => { getProjectMembers(task.projectId, { force: true }) }, [task.projectId])

  const assignee = members.find((m) => m.userId === task.assigneeId)

  return (
    <Dropdown
      trigger={
        <button className="flex items-center gap-1.5 hover:bg-surface-raised px-2 h-6 rounded-md transition-colors active:scale-[0.97]">
          {assignee ? (
            <>
              <Avatar user={{ name: assignee.name, avatar: assignee.avatar }} size="xs" />
              <span className="text-xs text-text">{assignee.name}</span>
            </>
          ) : (
            <span className="text-xs text-text-muted">Unassigned</span>
          )}
        </button>
      }
    >
      <button
        onClick={() => updateTask(task.id, { assigneeId: null })}
        className="w-full flex items-center gap-2.5 px-3 h-8 text-sm hover:bg-surface transition-colors"
      >
        <div className="w-5 h-5 rounded-full border border-border flex items-center justify-center text-[10px] text-text-faint flex-shrink-0">—</div>
        <span className="text-text-muted text-sm flex-1 text-left">Unassigned</span>
        {!task.assigneeId && <Check size={12} className="text-accent" />}
      </button>
      {members.map((member) => (
        <button
          key={member.userId}
          onClick={() => updateTask(task.id, { assigneeId: member.userId })}
          className="w-full flex items-center gap-2.5 px-3 h-8 text-sm hover:bg-surface transition-colors"
        >
          <Avatar user={{ name: member.name, avatar: member.avatar }} size="xs" />
          <span className="text-text-muted text-sm flex-1 text-left">{member.name}</span>
          {task.assigneeId === member.userId && <Check size={12} className="text-accent" />}
        </button>
      ))}
    </Dropdown>
  )
}

function PrioritySelector({ value, onChange }) {
  return (
    <Dropdown
      trigger={
        <button className="flex items-center gap-1.5 text-sm hover:bg-surface-raised px-2 h-6 rounded-md transition-colors active:scale-[0.97]">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: PRIORITIES[value]?.color }} />
          <span style={{ color: PRIORITIES[value]?.color }} className="text-xs font-medium">
            {PRIORITIES[value]?.label}
          </span>
        </button>
      }
    >
      {Object.entries(PRIORITIES).map(([key, p]) => (
        <button
          key={key}
          onClick={() => onChange(key)}
          className={cn(
            'w-full flex items-center gap-2.5 px-3 h-8 text-sm transition-colors',
            value === key ? 'bg-surface' : 'hover:bg-surface'
          )}
        >
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-text-muted text-sm">{p.label}</span>
          {value === key && <Check size={12} className="ml-auto text-accent" />}
        </button>
      ))}
    </Dropdown>
  )
}

// ── Activity entry renderer ───────────────────────────────────────────────────
function ActivityEntry({ entry, sections, projects }) {
  const { field, from, to, byName, at } = entry

  function renderField() {
    if (field === 'completed') {
      return to
        ? <span>marked this task <span className="text-[#34d399] font-medium">complete</span></span>
        : <span>marked this task <span className="text-text-muted font-medium">incomplete</span></span>
    }
    if (field === 'priority') {
      const fromP = PRIORITIES[from]
      const toP = PRIORITIES[to]
      return (
        <span>
          changed priority
          {from && from !== 'none' && <> from <span style={{ color: fromP?.color }} className="font-medium">{fromP?.label}</span></>}
          {' '}to <span style={{ color: toP?.color }} className="font-medium">{toP?.label || 'None'}</span>
        </span>
      )
    }
    if (field === 'dueDate') {
      if (!to) return <span>removed the due date</span>
      const d = new Date(to.includes('T') ? to : to + 'T00:00:00')
      if (isNaN(d.getTime())) return <span>set the due date</span>
      const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      return <span>set due date to <span className="font-medium text-text">{label}</span></span>
    }
    if (field === 'assigneeId') {
      if (!to) return <span>unassigned this task</span>
      return <span>reassigned this task</span>
    }
    if (field === 'rejected') {
      return (
        <span>
          sent this task back for revision
          {to && <span className="text-red-400"> — {to}</span>}
        </span>
      )
    }
    if (field === 'sectionId') {
      const toSection = sections.find((s) => s.id === to)
      const fromSection = sections.find((s) => s.id === from)
      return (
        <span>
          moved
          {fromSection && <> from <span className="font-medium text-text">{fromSection.name}</span></>}
          {toSection && <> to <span className="font-medium text-text">{toSection.name}</span></>}
        </span>
      )
    }
    return <span>updated {field}</span>
  }

  return (
    <div className="flex items-start gap-2 py-1">
      <div className="w-5 h-5 rounded-full bg-surface-raised border border-border flex items-center justify-center text-[8px] font-bold text-text-faint flex-shrink-0 mt-0.5">
        {byName?.[0]?.toUpperCase() || '?'}
      </div>
      <p className="text-[11px] text-text-faint leading-relaxed flex-1 pt-0.5">
        <span className="font-medium text-text-muted">{byName || 'Someone'}</span>{' '}
        {renderField()}
        {at && !isNaN(new Date(at).getTime()) && (
          <span className="ml-1.5 opacity-60">
            {new Date(at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        )}
      </p>
    </div>
  )
}

// ── Time entry renderer ───────────────────────────────────────────────────────
function TimeEntry({ entry, onDelete }) {
  const mins = entry.minutes || 0
  const h = Math.floor(mins / 60)
  const m = mins % 60
  const label = h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m}m`

  return (
    <div className="flex items-center gap-2 py-1 group">
      <div className="w-5 h-5 rounded-full bg-[#3d9bff]/10 border border-[#3d9bff]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Timer size={9} className="text-[#3d9bff]" />
      </div>
      <p className="text-[11px] text-text-faint leading-relaxed flex-1 pt-0.5">
        <span className="font-medium text-text-muted">{entry.byName || 'Someone'}</span>{' '}
        logged <span className="font-semibold text-[#3d9bff]">{label}</span>
        {entry.note && <span className="text-text-faint"> — {entry.note}</span>}
        <span className="ml-1.5 opacity-60">
          {new Date(entry.at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </span>
      </p>
      <button
        onClick={onDelete}
        className="text-text-faint hover:text-red-400 transition-colors flex-shrink-0 opacity-50 hover:opacity-100 p-1 -mr-1 rounded hover:bg-red-400/10 active:scale-[0.94]"
        title="Delete time entry"
      >
        <X size={12} />
      </button>
    </div>
  )
}

// ── Log Time form ─────────────────────────────────────────────────────────────
function LogTimeForm({ onSubmit, onCancel, initialHours = '', initialMins = '' }) {
  const [hours, setHours] = useState(initialHours)
  const [mins, setMins] = useState(initialMins)
  const [note, setNote] = useState('')
  const hoursRef = useRef()

  useEffect(() => { hoursRef.current?.focus() }, [])

  function submit() {
    const totalMins = (parseInt(hours || 0) * 60) + parseInt(mins || 0)
    if (totalMins <= 0) return
    onSubmit(totalMins, note.trim())
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.15, ease: [0.23, 1, 0.32, 1] }}
      className="bg-surface-raised border border-border rounded-xl p-3 space-y-2.5"
    >
      <p className="text-[10px] font-semibold text-text-faint uppercase tracking-wider">Log time</p>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1 bg-surface border border-border rounded-lg px-2 h-8 flex-1">
          <input
            ref={hoursRef}
            type="number"
            min="0"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') onCancel() }}
            placeholder="0"
            className="w-full bg-transparent text-sm text-text outline-none text-right"
          />
          <span className="text-xs text-text-faint flex-shrink-0">h</span>
        </div>
        <div className="flex items-center gap-1 bg-surface border border-border rounded-lg px-2 h-8 flex-1">
          <input
            type="number"
            min="0"
            max="59"
            value={mins}
            onChange={(e) => setMins(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') onCancel() }}
            placeholder="0"
            className="w-full bg-transparent text-sm text-text outline-none text-right"
          />
          <span className="text-xs text-text-faint flex-shrink-0">m</span>
        </div>
      </div>
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') onCancel() }}
        placeholder="Note (optional)"
        className="w-full bg-surface border border-border rounded-lg px-2.5 h-8 text-sm text-text placeholder:text-text-faint outline-none focus:border-accent/40 transition-colors"
      />
      <div className="flex gap-2">
        <button
          onClick={submit}
          disabled={(parseInt(hours || 0) * 60 + parseInt(mins || 0)) <= 0}
          className="flex-1 h-7 rounded-lg bg-accent text-black text-xs font-semibold hover:opacity-90 transition-opacity active:scale-[0.97] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Log
        </button>
        <button
          onClick={onCancel}
          className="px-3 h-7 rounded-lg bg-surface border border-border text-xs text-text-muted hover:text-text transition-colors active:scale-[0.97]"
        >
          Cancel
        </button>
      </div>
    </motion.div>
  )
}

// ── Recurrence selector ───────────────────────────────────────────────────────
function RecurrenceSelector({ value, onChange }) {
  const current = recurrenceKey(value)
  return (
    <Dropdown
      trigger={
        <button className="flex items-center gap-1.5 text-sm hover:bg-surface-raised px-2 h-6 rounded-md transition-colors active:scale-[0.97]">
          {value?.freq ? (
            <RotateCcw size={11} className="text-accent flex-shrink-0" />
          ) : (
            <RotateCcw size={11} className="text-text-faint flex-shrink-0" />
          )}
          <span className={cn('text-xs', value?.freq ? 'text-accent font-medium' : 'text-text-muted')}>
            {recurrenceLabel(value)}
          </span>
        </button>
      }
    >
      {RECURRENCE_OPTIONS.map((opt) => (
        <button
          key={opt.key}
          onClick={() => onChange(opt.value)}
          className={cn(
            'w-full flex items-center gap-2.5 px-3 h-8 text-sm transition-colors',
            current === opt.key ? 'bg-surface' : 'hover:bg-surface'
          )}
        >
          <RotateCcw size={11} className={opt.value ? 'text-accent' : 'text-text-faint'} />
          <span className="text-text-muted text-sm flex-1 text-left">{opt.label}</span>
          {current === opt.key && <Check size={12} className="text-accent" />}
        </button>
      ))}
    </Dropdown>
  )
}

// ── Content size selector ─────────────────────────────────────────────────────
const CONTENT_SIZES = [
  { key: 'story',     label: 'Reel',         desc: '1080×1920' },
  { key: 'infeed',    label: 'In-Feed',      desc: '2160×2700' },
  { key: 'landscape', label: 'Landscape',    desc: '1920×1080' },
]

function SizePicker({ values, onChange }) {
  function toggle(key) {
    const next = values.includes(key) ? values.filter((v) => v !== key) : [...values, key]
    onChange(next)
  }
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {CONTENT_SIZES.map((s) => {
        const active = values.includes(s.key)
        return (
          <button
            key={s.key}
            onClick={() => toggle(s.key)}
            title={s.desc}
            className={cn(
              'text-[10px] font-semibold px-2 py-0.5 rounded-full border transition-all duration-150 active:scale-[0.97]',
              active
                ? 'bg-accent/15 border-accent/40 text-accent'
                : 'bg-surface-raised border-border/60 text-text-muted hover:border-text-muted hover:text-text'
            )}
          >
            {s.label}
          </button>
        )
      })}
    </div>
  )
}

// ── @ mention helpers ─────────────────────────────────────────────────────────
function renderCommentText(text, members) {
  if (!text) return null
  // Build mention pattern from actual member names (longest first)
  let mentionPat = '@\\w[\\w ]*\\w|@\\w+'
  if (members?.length) {
    const escaped = [...members]
      .map((m) => m.name?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .filter(Boolean)
      .sort((a, b) => b.length - a.length)
    if (escaped.length) mentionPat = `@(?:${escaped.join('|')})`
  }
  // Combined: URLs first (greedy), then mentions
  const pattern = new RegExp(`(https?:\\/\\/[^\\s<>'"]+[^\\s<>'".,!?)]|${mentionPat})`, 'g')
  return text.split(pattern).map((part, i) => {
    if (/^https?:\/\//.test(part)) {
      return (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-accent underline underline-offset-2 hover:opacity-80 break-all"
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      )
    }
    if (part.startsWith('@')) return <span key={i} className="font-medium text-accent">{part}</span>
    return part
  })
}

function SubtaskRow({ child, grandchildren, onOpen, onToggle, onDelete, onDateChange, openCtx }) {
  const ctxItems = [
    { label: 'Open subtask', icon: ArrowUpRight, onClick: onOpen },
    { separator: true },
    { label: child.completed ? 'Mark incomplete' : 'Mark complete', icon: child.completed ? RotateCcw : Check, onClick: onToggle },
    { separator: true },
    { label: 'Delete subtask', icon: Trash2, onClick: onDelete, danger: true },
  ]

  const longPress = useLongPress((x, y) => {
    openCtx({ clientX: x, clientY: y, preventDefault: () => {}, stopPropagation: () => {} }, ctxItems)
  })

  return (
    <div
      className="flex items-center gap-3 group py-1.5 min-h-[40px]"
      onContextMenu={(e) => openCtx(e, ctxItems)}
      {...longPress}
    >
      <TaskCheckbox
        completed={child.completed}
        onClick={(e) => { e.stopPropagation(); onToggle() }}
        size="md"
      />

      <button
        onClick={onOpen}
        className={cn(
          'text-sm flex-1 text-left hover:text-accent transition-colors leading-snug',
          child.completed && 'line-through text-text-muted'
        )}
      >
        {child.title}
      </button>

      {grandchildren > 0 && (
        <span className="text-[10px] text-text-faint opacity-60 flex-shrink-0">{grandchildren}</span>
      )}

      {/* Due date */}
      <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
        <DatePicker
          value={child.dueDate}
          onChange={onDateChange}
          placeholder=""
        />
      </div>

      <button
        onClick={(e) => { e.stopPropagation(); onDelete() }}
        className="opacity-0 group-hover:opacity-100 max-md:opacity-100 text-text-faint hover:text-red-400 transition-all flex-shrink-0"
      >
        <X size={13} />
      </button>
    </div>
  )
}

export function TaskDetail({ taskId }) {
  const { tasks, projects, sections, currentUser, updateTask, toggleTask, initiateCompletion, approveTask, rejectTask, setSelectedTask, deleteTask, duplicateTask, addTask, spawnNextRecurrence, undo, getProjectMembers, notifyMention, _membersCache, ensureMembers, uploadAttachment, uploadAttachmentThumb, deleteAttachmentFromStorage } = useAppStore()
  const toast = useToast()
  const openCtx = useContextMenu()
  const task = tasks.find((t) => t.id === taskId)
  // Must be declared before any hooks that reference it (avoids TDZ in deps arrays)
  const imageAttachments = (task?.attachments || []).filter((a) => (a.isImage || a.isVideo) && a.url && !a.uploading)
  const [editingTitle, setEditingTitle] = useState(false)
  const [titleVal, setTitleVal] = useState(task?.title || '')
  const [newSubtask, setNewSubtask] = useState('')
  const [newTag, setNewTag] = useState('')
  const [addingTag, setAddingTag] = useState(false)
  const [newComment, setNewComment] = useState('')
  const [localDesc, setLocalDesc] = useState(task?.description || '')
  const [editingDesc, setEditingDesc] = useState(false)
  const descDirty = useRef(false)
  const descTimer = useRef(null)
  const [lightboxIdx, setLightboxIdx] = useState(null)
  const lightboxVideoRef = useRef(null)
  const [lightboxVideoPaused, setLightboxVideoPaused] = useState(false)
  const [lightboxIconVisible, setLightboxIconVisible] = useState(false)
  const lightboxIconTimerRef = useRef(null)
  const [draggingFile, setDraggingFile] = useState(false)
  const [draggingInspo, setDraggingInspo] = useState(false)
  const [loggingTime, setLoggingTime] = useState(false)
  const [timerLogSeed, setTimerLogSeed] = useState(null) // { hours, mins } pre-filled from timer
  const [rejectingTask, setRejectingTask] = useState(false)
  const [rejectNote, setRejectNote] = useState('')
  const [showMoreFields, setShowMoreFields] = useState(false)
  const [activityOpen, setActivityOpen] = useState(false)
  const [addingInspo, setAddingInspo] = useState(false)
  const [inspoUrl, setInspoUrl] = useState('')
  const [timerStartedAt, setTimerStartedAt] = useState(null)
  const [timerTick, setTimerTick] = useState(0)
  const timerIntervalRef = useRef(null)
  const [editingCommentId, setEditingCommentId] = useState(null)
  const [editingCommentText, setEditingCommentText] = useState('')
  const [pendingUploads, setPendingUploads] = useState([])
  // Clear drag states when drag ends outside the window (no onDragLeave fires)
  useEffect(() => {
    function clearDrag() { setDraggingFile(false); setDraggingInspo(false) }
    function onDocDragLeave(e) { if (!e.relatedTarget) clearDrag() }
    document.addEventListener('dragend', clearDrag)
    document.addEventListener('dragleave', onDocDragLeave)
    return () => {
      document.removeEventListener('dragend', clearDrag)
      document.removeEventListener('dragleave', onDocDragLeave)
    }
  }, [])
  // Self-heal: if cache is empty for this project, trigger background fetch
  useEffect(() => {
    if (task?.projectId) ensureMembers(task.projectId)
  }, [task?.projectId])
  // Read members reactively from store cache; trigger fetch if not cached yet
  const cachedMembers = _membersCache[task?.projectId] || []
  const members = cachedMembers.length > 0 ? cachedMembers
    : currentUser ? [{ userId: currentUser.id, name: currentUser.name || 'Me', email: currentUser.email || '', avatar: currentUser.avatar || null, role: 'owner' }]
    : []
  // @ mention state
  const [mentionQuery, setMentionQuery] = useState('')
  const [mentionOpen, setMentionOpen] = useState(false)
  const [mentionIndex, setMentionIndex] = useState(0)
  const [mentionPos, setMentionPos] = useState({ top: 0, left: 0 })
  const titleRef = useRef()
  const descRef = useRef()
  const descClickOffset = useRef(null)
  const fileInputRef = useRef()
  const inspoFileRef = useRef()
  const commentRef = useRef()
  const mentionAnchorRef = useRef(0) // position of the @ character
  const mentionTargetRef = useRef('comment') // 'comment' | 'description'
  const lastDescMentionCheck = useRef('') // track last description we checked for mentions

  useEffect(() => {
    if (task?.projectId) getProjectMembers(task.projectId, { force: true })
  }, [task?.projectId])

  // Clear in-progress upload placeholders when switching tasks
  useEffect(() => { setPendingUploads([]) }, [taskId])

  // Prevent browser from opening/navigating to dropped files at the document level.
  // Without this, the browser ignores React's onDragOver preventDefault and opens a file picker.
  useEffect(() => {
    const prevent = (e) => e.preventDefault()
    document.addEventListener('dragover', prevent)
    document.addEventListener('drop', prevent)
    return () => {
      document.removeEventListener('dragover', prevent)
      document.removeEventListener('drop', prevent)
    }
  }, [])

  // Sync local description from store — but only when not actively typing
  useEffect(() => {
    if (!descDirty.current) setLocalDesc(task?.description || '')
  }, [task?.id, task?.description])

  // Auto-resize description textarea — runs on content change AND when entering edit mode
  useEffect(() => {
    const el = descRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = el.scrollHeight + 'px'
  }, [localDesc, editingDesc])

  useEffect(() => {
    if (task) setTitleVal(task.title)
  }, [task?.title])

  // Live timer interval
  useEffect(() => {
    if (timerStartedAt) {
      timerIntervalRef.current = setInterval(() => setTimerTick((t) => t + 1), 1000)
    } else {
      clearInterval(timerIntervalRef.current)
    }
    return () => clearInterval(timerIntervalRef.current)
  }, [timerStartedAt])

  // Lightbox keyboard navigation + cleanup
  useEffect(() => {
    if (lightboxIdx === null) {
      setLightboxVideoPaused(false)
      setLightboxIconVisible(false)
      clearTimeout(lightboxIconTimerRef.current)
      return
    }
    const handler = (e) => {
      if (e.key === 'Escape') setLightboxIdx(null)
      if (e.key === 'ArrowRight') setLightboxIdx((i) => Math.min(i + 1, imageAttachments.length - 1))
      if (e.key === 'ArrowLeft') setLightboxIdx((i) => Math.max(i - 1, 0))
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [lightboxIdx, imageAttachments.length])

  if (!task) return null

  const project = projects.find((p) => p.id === task.projectId)
  const section = sections.find((s) => s.id === task.sectionId)
  const isApproval = section?.type === 'approval'
  const sectionApprovers = section?.approvers || []
  const canApprove = sectionApprovers.length > 0
    ? sectionApprovers.includes(currentUser?.id)
    : (project?.role === 'owner' || project?.ownerId === currentUser?.id || project?.myPermissions?.approve_tasks)
  const allEntries = task.comments || []
  const attachments = [...(task.attachments || []), ...pendingUploads]
  // imageAttachments declared before hooks above (avoids TDZ)
  const isPdf = (att) => att.name?.toLowerCase().endsWith('.pdf')
  const tags = task.tags || []
  const childTasks = tasks.filter((t) => t.parentId === task.id)
  const parentTask = task.parentId ? tasks.find((t) => t.id === task.parentId) : null

  // Derived counts
  const regularComments = allEntries.filter((e) => !e.type || e.type === 'comment')
  const timeEntries = allEntries.filter((e) => e.type === 'time')
  const totalMinutes = timeEntries.reduce((sum, e) => sum + (e.minutes || 0), 0)
  const totalTimeLabel = totalMinutes > 0
    ? (Math.floor(totalMinutes / 60) > 0
        ? (totalMinutes % 60 > 0
            ? `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`
            : `${Math.floor(totalMinutes / 60)}h`)
        : `${totalMinutes}m`)
    : null

  // Live timer display — timerTick forces re-render every second
  void timerTick
  const timerSeconds = timerStartedAt ? Math.floor((Date.now() - timerStartedAt) / 1000) : 0
  const timerDisplay = timerStartedAt
    ? [Math.floor(timerSeconds / 3600), Math.floor((timerSeconds % 3600) / 60), timerSeconds % 60]
        .map((n, i) => i === 0 && n === 0 ? null : String(n).padStart(2, '0'))
        .filter(Boolean)
        .join(':')
    : null

  function startTimer() { setTimerStartedAt(Date.now()); setTimerTick(0) }
  function stopTimer() {
    if (!timerStartedAt) return
    const totalSecs = Math.floor((Date.now() - timerStartedAt) / 1000)
    const h = Math.floor(totalSecs / 3600)
    const m = Math.floor((totalSecs % 3600) / 60)
    setTimerStartedAt(null)
    setTimerLogSeed({ hours: h > 0 ? String(h) : '', mins: m > 0 ? String(m) : '' })
    setLoggingTime(true)
  }

  function saveCommentEdit(id) {
    const text = editingCommentText.trim()
    if (!text) return
    updateTask(task.id, { comments: allEntries.map((c) => c.id === id ? { ...c, text } : c) })
    setEditingCommentId(null)
  }

  // Comments always visible (sorted oldest → newest)
  const sortedComments = [...regularComments]
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))

  // Activity feed = audit trail + time entries only (no comments)
  const activityEntries = allEntries
    .filter((e) => e.type === 'activity' || e.type === 'time')
    .sort((a, b) => new Date(a.createdAt || a.at) - new Date(b.createdAt || b.at))

  // Unified sorted timeline (kept for legacy — unused after refactor)
  const timeline = activityEntries

  // Filtered mention suggestions — use currentUser as fallback if members haven't loaded
  const memberPool = members.length > 0 ? members : (currentUser ? [{ userId: currentUser.id, name: currentUser.name, email: currentUser.email, avatar: currentUser.avatar }] : [])
  const mentionSuggestions = mentionOpen
    ? (mentionQuery ? memberPool.filter((m) => m.name?.toLowerCase().includes(mentionQuery.toLowerCase())) : memberPool).slice(0, 6)
    : []

  function saveTitle() {
    if (titleVal.trim()) updateTask(task.id, { title: titleVal.trim() })
    setEditingTitle(false)
  }

  function addSubtask() {
    if (!newSubtask.trim()) return
    addTask({ title: newSubtask.trim(), parentId: task.id, projectId: task.projectId, sectionId: task.sectionId, assigneeId: null })
    setNewSubtask('')
  }

  function detectMention(val, cursor, ref, target) {
    const textBefore = val.slice(0, cursor)
    // Allow spaces in names (e.g. "John Doe") — match @Word or @Word Word at end of input
    const atMatch = textBefore.match(/@([\w]+(?:\s[\w]*)?)$/)
    if (atMatch) {
      mentionAnchorRef.current = cursor - atMatch[0].length
      mentionTargetRef.current = target
      setMentionQuery(atMatch[1].trim())
      setMentionIndex(0)
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect()
        // Position below the textarea; flip above if too close to viewport bottom
        const spaceBelow = window.innerHeight - rect.bottom
        if (spaceBelow < 220) {
          setMentionPos({ top: rect.top - 4, left: rect.left + 8, flipUp: true })
        } else {
          setMentionPos({ top: rect.bottom + 4, left: rect.left + 8, flipUp: false })
        }
      }
      setMentionOpen(true)
    } else {
      setMentionOpen(false)
    }
  }

  function handleCommentChange(e) {
    const val = e.target.value
    setNewComment(val)
    detectMention(val, e.target.selectionStart, commentRef, 'comment')
  }

  function handleDescriptionChange(e) {
    const val = e.target.value
    setLocalDesc(val)
    descDirty.current = true
    detectMention(val, e.target.selectionStart, descRef, 'description')
    // Debounce DB write — save 600ms after last keystroke
    clearTimeout(descTimer.current)
    descTimer.current = setTimeout(() => {
      descDirty.current = false
      updateTask(task.id, { description: val })
      // Notify members mentioned in description (only new mentions since last check)
      const prev = lastDescMentionCheck.current
      lastDescMentionCheck.current = val
      for (const member of memberPool) {
        if (!member.name) continue
        if (member.userId === currentUser?.id) continue
        const tag = '@' + member.name.toLowerCase()
        const wasIn = prev.toLowerCase().includes(tag)
        const nowIn = val.toLowerCase().includes(tag)
        if (nowIn && !wasIn) {
          notifyMention(member.userId, {
            taskId: task.id,
            projectId: task.projectId,
            byName: currentUser?.name,
            taskTitle: task.title,
          })
        }
      }
    }, 600)
  }

  function insertMention(member) {
    const isDesc = mentionTargetRef.current === 'description'
    const ref = isDesc ? descRef : commentRef
    const currentVal = isDesc ? localDesc : newComment
    const anchor = mentionAnchorRef.current
    const before = currentVal.slice(0, anchor)
    const after = currentVal.slice(anchor + mentionQuery.length + 1)
    const inserted = `@${member.name} `
    const newVal = before + inserted + after
    if (isDesc) {
      setLocalDesc(newVal)
      descDirty.current = false
      updateTask(task.id, { description: newVal })
    } else {
      setNewComment(newVal)
    }
    setMentionOpen(false)
    setMentionQuery('')
    setTimeout(() => {
      if (ref.current) {
        const pos = before.length + inserted.length
        ref.current.focus()
        ref.current.setSelectionRange(pos, pos)
      }
    }, 0)
  }

  function handleCommentKeyDown(e) {
    if (mentionOpen && mentionSuggestions.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex((i) => (i + 1) % mentionSuggestions.length); return }
      if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIndex((i) => (i - 1 + mentionSuggestions.length) % mentionSuggestions.length); return }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(mentionSuggestions[mentionIndex]); return }
      if (e.key === 'Escape') { setMentionOpen(false); return }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addComment() }
  }

  function addComment() {
    if (!newComment.trim()) return
    const text = newComment.trim()
    const comment = {
      id: 'c' + Date.now(),
      text,
      author: currentUser?.name,
      authorId: currentUser?.id,
      createdAt: new Date().toISOString(),
    }
    updateTask(task.id, { comments: [...allEntries, comment] })
    setNewComment('')
    setMentionOpen(false)

    // Notify @mentioned members — match against actual member names (handles multi-word names)
    for (const member of memberPool) {
      if (!member.name) continue
      if (member.userId === currentUser?.id) continue
      if (text.toLowerCase().includes('@' + member.name.toLowerCase())) {
        notifyMention(member.userId, {
          taskId: task.id,
          projectId: task.projectId,
          byName: currentUser?.name,
          taskTitle: task.title,
        })
      }
    }
  }

  function deleteEntry(entryId) {
    updateTask(task.id, { comments: allEntries.filter((c) => c.id !== entryId) })
  }

  function logTime(minutes, note) {
    const entry = {
      id: 'time' + Date.now(),
      type: 'time',
      minutes,
      note,
      by: currentUser?.id,
      byName: currentUser?.name,
      at: new Date().toISOString(),
    }
    updateTask(task.id, { comments: [...allEntries, entry] })
    setLoggingTime(false)
    setTimerLogSeed(null)
    const h = Math.floor(minutes / 60)
    const m = minutes % 60
    const label = h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m}m`
    toast({ message: `Logged ${label}` })
  }

  function FileTypeIcon({ name, size = 12 }) {
    const ext = name?.split('.').pop()?.toLowerCase()
    if (ext === 'pdf') return <FileText size={size} className="text-red-400 flex-shrink-0" />
    if (['svg', 'xml', 'html', 'css', 'js', 'ts'].includes(ext)) return <Code size={size} className="text-blue-400 flex-shrink-0" />
    if (['doc', 'docx', 'txt'].includes(ext)) return <FileText size={size} className="text-text-faint flex-shrink-0" />
    return <Link2 size={size} className="text-text-faint flex-shrink-0" />
  }

  async function handleFiles(files) {
    for (const file of Array.from(files)) {
      const isImage = file.type.startsWith('image/') || file.type === 'image/svg+xml' || file.name.endsWith('.svg')
      const isVideo = file.type.startsWith('video/')
      const attId = 'a' + Date.now() + Math.random().toString(36).slice(2)
      setPendingUploads((prev) => [...prev, { id: attId, name: file.name, isImage, isVideo, uploading: true, progress: 0, url: null }])

      // Kick off thumb generation in parallel with the original upload. We
      // already have the File in memory, so this runs on the local CPU while
      // the bytes are still going over the wire.
      const thumbBlobPromise = isVideo
        ? generateVideoThumb(file)
        : (isImage ? generateImageThumb(file) : Promise.resolve(null))

      const result = await uploadAttachment(file, task.id, (loaded, total) => {
        if (total > 0) setPendingUploads((prev) => prev.map((p) => p.id === attId ? { ...p, progress: Math.round(loaded / total * 100) } : p))
      })
      const url = result?.url || (typeof result === 'string' ? result : null)
      setPendingUploads((prev) => prev.filter((p) => p.id !== attId))

      if (!url) {
        const msg = result === null ? 'Not connected to Supabase' : (result?.error || 'Unknown error')
        console.error('[attachment upload]', file.name, msg, result)
        toast({ message: `Upload failed: ${msg}` })
        continue
      }

      // Wait for thumb gen — it usually finishes well before the original
      // upload on anything larger than a small photo, so this typically
      // returns immediately. Falls back to null on codec / decode errors.
      let thumbBlob = null
      try {
        thumbBlob = await thumbBlobPromise
      } catch (e) {
        console.warn('[attachment thumb gen]', file.name, e?.message)
      }

      // Persist the attachment immediately with the original URL — we want
      // the user to see the upload land in the task right away even if the
      // thumb upload is still in flight (or ultimately fails).
      const baseAtt = { id: attId, name: file.name, isImage, isVideo, uploading: false, url }
      const current = useAppStore.getState().tasks.find((t) => t.id === task.id)
      updateTask(task.id, {
        attachments: [
          ...(current?.attachments || []),
          thumbBlob ? { ...baseAtt, thumbUploading: true } : baseAtt,
        ],
      })

      if (!thumbBlob) continue

      // Upload the thumb and patch it in. uploadAttachmentThumb routes through
      // the same XHR + auth path as the main upload — no extra TUS risk.
      const thumbResult = await uploadAttachmentThumb(thumbBlob, task.id, attId)
      const thumbUrl = thumbResult?.url || null
      const fresh = useAppStore.getState().tasks.find((t) => t.id === task.id)
      if (!fresh?.attachments) continue
      updateTask(task.id, {
        attachments: fresh.attachments.map((a) => {
          if (a.id !== attId) return a
          // Drop the thumbUploading flag either way; only set thumbUrl on success.
          const { thumbUploading, ...rest } = a
          return thumbUrl ? { ...rest, thumbUrl } : rest
        }),
      })
    }
  }

  function removeAttachment(aId) {
    const att = attachments.find((a) => a.id === aId)
    // Only write persisted attachments back (not in-progress pendingUploads)
    updateTask(task.id, { attachments: (task.attachments || []).filter((a) => a.id !== aId) })
    // Delete from Supabase storage for real uploaded files (not inspo link cards).
    // Also nuke the companion thumbnail if one exists — deleteAttachmentFromStorage
    // works on any attachments-bucket URL, including the /thumbs/ subpath.
    if (att?.url && att.type !== 'inspo') deleteAttachmentFromStorage(att.url)
    if (att?.thumbUrl && att.type !== 'inspo') deleteAttachmentFromStorage(att.thumbUrl)
  }

  function addTag() {
    if (!newTag.trim() || tags.includes(newTag.trim())) return
    updateTask(task.id, { tags: [...tags, newTag.trim()] })
    setNewTag('')
    setAddingTag(false)
  }

  function removeTag(tag) {
    updateTask(task.id, { tags: tags.filter((t) => t !== tag) })
  }

  function addInspoLink(url) {
    const trimmed = url.trim()
    if (!trimmed) return
    setInspoUrl('')
    setAddingInspo(false)
    const id = 'inspo' + Date.now()
    let host = ''
    try { host = new URL(trimmed).hostname } catch {}
    const favicon = host ? `https://www.google.com/s2/favicons?domain=${host}&sz=32` : null
    const current = useAppStore.getState().tasks.find((t) => t.id === task.id)
    updateTask(task.id, {
      attachments: [...(current?.attachments || []), { id, type: 'inspo', url: trimmed, title: trimmed, image: null, favicon, loading: false }],
    })
  }

  async function handleInspoFiles(files) {
    for (const file of Array.from(files)) {
      const isImage = file.type.startsWith('image/')
      const id = 'inspo' + Date.now() + Math.random().toString(36).slice(2)
      const placeholder = { id, type: 'inspo', isFile: true, name: file.name, isImage, url: null, image: null, loading: true }
      // Show loading state via local state only — no store write until URL is ready
      setPendingUploads((prev) => [...prev, placeholder])
      const result = await uploadAttachment(file, task.id)
      const url = result?.url || (typeof result === 'string' ? result : null)
      setPendingUploads((prev) => prev.filter((p) => p.id !== id))
      if (url) {
        const current = useAppStore.getState().tasks.find((t) => t.id === task.id)
        updateTask(task.id, { attachments: [...(current?.attachments || []), { ...placeholder, url, image: isImage ? url : null, loading: false }] })
      } else {
        toast({ message: result?.error ? `Failed to upload: ${result.error}` : `Failed to upload ${file.name}` })
      }
    }
  }

  function handleFileDrop(e) {
    e.preventDefault()
    setDraggingFile(false)
    handleFiles(e.dataTransfer.files)
  }

  return (
    <>
    <motion.div
      key={taskId}
      initial={{ x: 24, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 24, opacity: 0 }}
      transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
      role="complementary"
      aria-label="Task details"
      className="w-full h-full bg-transparent overflow-y-auto flex flex-col relative"
      onDragEnter={(e) => { e.preventDefault(); if (e.dataTransfer.types.includes('Files')) setDraggingFile(true) }}
      onDragOver={(e) => { e.preventDefault(); if (e.dataTransfer.types.includes('Files')) setDraggingFile(true) }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDraggingFile(false) }}
      onDrop={handleFileDrop}
    >
      {/* File drop overlay */}
      {draggingFile && (
        <div className="absolute inset-0 z-50 bg-accent/5 border-2 border-dashed border-accent/40 rounded-none flex items-center justify-center pointer-events-none">
          <div className="flex flex-col items-center gap-2">
            <Paperclip size={24} className="text-accent/60" />
            <p className="text-sm font-medium text-accent/80">Drop to attach</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06] sticky top-0 bg-transparent z-10">
        <div className="flex items-center gap-2 text-xs text-text-muted min-w-0">
          {project && (
            <>
              <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: project.color }} />
              <span className="truncate">{project.name}</span>
              <ChevronRight size={10} className="flex-shrink-0" />
              <span className="truncate">{section?.name}</span>
            </>
          )}
          {parentTask && (
            <>
              <ChevronRight size={10} className="flex-shrink-0" />
              <button
                onClick={() => setSelectedTask(parentTask.id)}
                className="truncate text-accent hover:underline max-w-[120px]"
              >
                {parentTask.title}
              </button>
            </>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
          {/* Delete button — always visible on mobile for easy access */}
          <button
            onClick={() => {
              const title = task.title
              deleteTask(task.id)
              setSelectedTask(null)
              toast({ message: `"${title}" deleted`, action: { label: 'Undo', onClick: undo } })
            }}
            className="md:hidden w-8 h-8 flex items-center justify-center rounded-lg text-text-faint hover:text-red-400 hover:bg-red-500/10 transition-colors active:scale-[0.88]"
            aria-label="Delete task"
          >
            <Trash2 size={15} />
          </button>
          <Dropdown
            align="right"
            trigger={
              <button className="text-text-faint hover:text-text-muted transition-colors p-1 rounded hover:bg-surface-raised">
                <MoreHorizontal size={14} />
              </button>
            }
          >
            <DropdownItem
              icon={Copy}
              label="Duplicate task"
              onClick={() => {
                duplicateTask(task.id)
                toast({ message: 'Task duplicated' })
              }}
            />
            <DropdownSeparator />
            <DropdownItem
              icon={Trash2}
              label="Delete task"
              danger
              onClick={() => {
                const title = task.title
                deleteTask(task.id)
                setSelectedTask(null)
                toast({ message: `"${title}" deleted`, action: { label: 'Undo', onClick: undo } })
              }}
            />
          </Dropdown>
          <button
            onClick={() => setSelectedTask(null)}
            className="text-text-faint hover:text-text-muted transition-colors p-1 rounded hover:bg-surface-raised active:scale-[0.97]"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      <div className="px-5 py-4 space-y-5 flex-1">
        {/* Complete + Title */}
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <TaskCheckbox
              size="md"
              completed={task.completed}
              pendingApproval={isApproval && !task.completed}
              onApprove={isApproval && canApprove && !task.completed ? (e) => approveTask(task.id, { x: e.clientX, y: e.clientY }) : undefined}
              onClick={(e) => {
                if (task.completed) toggleTask(task.id, { x: e.clientX, y: e.clientY })
                else initiateCompletion(task.id, { x: e.clientX, y: e.clientY })
              }}
            />
          </div>

          {editingTitle ? (
            <input
              ref={titleRef}
              autoFocus
              value={titleVal}
              onChange={(e) => setTitleVal(e.target.value)}
              onBlur={saveTitle}
              onKeyDown={(e) => { if (e.key === 'Enter') saveTitle(); if (e.key === 'Escape') { setTitleVal(task.title); setEditingTitle(false) } }}
              className="flex-1 text-base font-semibold text-text bg-transparent outline-none border-b border-accent/40 pb-0.5"
            />
          ) : (
            <h2
              onClick={() => setEditingTitle(true)}
              className={cn(
                'flex-1 text-base font-semibold cursor-text leading-snug',
                task.completed ? 'line-through text-text-muted' : 'text-text'
              )}
            >
              {task.title}
            </h2>
          )}
        </div>

        {/* Approval banner */}
        {isApproval && !task.completed && (
          <div className="rounded-xl bg-[#ff9500]/[0.08] border border-[#ff9500]/20 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2.5">
              <span className="text-xs font-medium text-[#ff9500]">Pending approval</span>
              {canApprove ? (
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => { setRejectingTask((v) => !v); setRejectNote('') }}
                    className="flex items-center gap-1.5 px-3 h-7 rounded-lg bg-red-500/10 text-red-400 text-xs font-semibold hover:bg-red-500/20 transition-colors active:scale-[0.97]"
                  >
                    <X size={11} /> Reject
                  </button>
                  <button
                    onClick={(e) => { approveTask(task.id, { x: e.clientX, y: e.clientY }); setRejectingTask(false) }}
                    className="flex items-center gap-1.5 px-3 h-7 rounded-lg bg-[#34d399]/10 text-[#34d399] text-xs font-semibold hover:bg-[#34d399]/20 transition-colors active:scale-[0.97]"
                  >
                    <Check size={11} /> Approve
                  </button>
                </div>
              ) : (
                <span className="text-[11px] text-[#ff9500]/70">Awaiting sign-off</span>
              )}
            </div>
            <AnimatePresence>
              {rejectingTask && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.15, ease: [0.23, 1, 0.32, 1] }}
                  className="overflow-hidden border-t border-[#ff9500]/20"
                >
                  <div className="px-3 pb-3 pt-2.5 flex gap-2">
                    <input
                      autoFocus
                      value={rejectNote}
                      onChange={(e) => setRejectNote(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { rejectTask(task.id, rejectNote.trim()); setRejectingTask(false); setRejectNote(''); toast({ message: 'Task sent back for revision' }) }
                        if (e.key === 'Escape') { setRejectingTask(false); setRejectNote('') }
                      }}
                      placeholder="Reason for rejection (optional)…"
                      className="flex-1 bg-surface border border-border rounded-lg px-2.5 h-8 text-xs text-text placeholder:text-text-faint outline-none focus:border-red-400/40 transition-colors"
                    />
                    <button
                      onClick={() => { rejectTask(task.id, rejectNote.trim()); setRejectingTask(false); setRejectNote(''); toast({ message: 'Task sent back for revision' }) }}
                      className="px-3 h-8 rounded-lg bg-red-500/15 text-red-400 text-xs font-semibold hover:bg-red-500/25 transition-colors active:scale-[0.97] flex-shrink-0"
                    >
                      Send back
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Fields — always-visible core */}
        <div className="divide-y divide-border/40">
          <DetailRow icon={User} label="Assignee">
            <AssigneeSelector task={task} />
          </DetailRow>

          <DetailRow icon={Calendar} label="Due date">
            <DatePicker
              value={task.dueDate}
              onChange={(val) => updateTask(task.id, { dueDate: val })}
              placeholder="Set due date"
            />
          </DetailRow>

          <DetailRow icon={AlignLeft} label="Project">
            <div className="flex items-center gap-1.5 flex-wrap">
              {project && (
                <>
                  <span className="flex items-center gap-1.5 text-sm text-text">
                    <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: project.color }} />
                    {project.name}
                  </span>
                  <ChevronRight size={11} className="text-text-faint flex-shrink-0" />
                </>
              )}
              <Dropdown
                align="left"
                trigger={
                  <button className="flex items-center gap-1 text-sm text-text hover:text-accent transition-colors active:scale-[0.97]">
                    <span>{section?.name || 'None'}</span>
                    <ChevronDown size={11} className="text-text-faint" />
                  </button>
                }
              >
                {sections
                  .filter((s) => s.projectId === task.projectId)
                  .sort((a, b) => { const r = (s) => s.type === 'approval' ? 1 : s.type === 'complete' ? 2 : 0; return r(a) - r(b) || a.order - b.order })
                  .map((s) => (
                    <DropdownItem
                      key={s.id}
                      label={s.name}
                      onClick={() => updateTask(task.id, { sectionId: s.id })}
                    />
                  ))}
              </Dropdown>
            </div>
          </DetailRow>

          <DetailRow icon={Crop} label="Size">
            <SizePicker
              values={task.contentSizes || []}
              onChange={(val) => updateTask(task.id, { contentSizes: val })}
            />
          </DetailRow>
        </div>

        {/* More fields toggle */}
        <button
          onClick={() => setShowMoreFields((v) => !v)}
          className="flex items-center gap-1.5 text-xs font-medium text-text-muted uppercase tracking-wider hover:text-text transition-colors active:scale-[0.97] -mt-1"
        >
          <ChevronDown size={12} className={cn('transition-transform duration-200', showMoreFields && 'rotate-180')} />
          {showMoreFields ? 'Fewer fields' : 'More fields'}
        </button>

        {/* More fields — collapsible */}
        <AnimatePresence initial={false}>
          {showMoreFields && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
              className="overflow-hidden"
            >
              <div className="divide-y divide-border/40 border-t border-border/40">
                <DetailRow icon={Flag} label="Priority">
                  <PrioritySelector
                    value={task.priority || 'none'}
                    onChange={(val) => updateTask(task.id, { priority: val })}
                  />
                </DetailRow>

                {/* Time tracked */}
                <DetailRow icon={Clock} label="Time logged">
                  <div className="flex items-center gap-2 flex-wrap">
                    {totalTimeLabel && (
                      <span className="text-xs font-semibold text-[#3d9bff]">{totalTimeLabel}</span>
                    )}
                    {timerDisplay && (
                      <span className="text-xs font-mono font-semibold text-red-400 tabular-nums">● {timerDisplay}</span>
                    )}
                    {timerStartedAt ? (
                      <button
                        onClick={stopTimer}
                        className="flex items-center gap-1 text-[10px] text-red-400 hover:text-red-300 transition-colors px-1.5 h-5 rounded border border-red-400/40 hover:border-red-400/70 active:scale-[0.97]"
                      >
                        Stop
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={startTimer}
                          className="flex items-center gap-1 text-[10px] text-text-faint hover:text-accent transition-colors px-1.5 h-5 rounded border border-border hover:border-accent/40 active:scale-[0.97]"
                          title="Start timer"
                        >
                          <Timer size={9} />
                          Start
                        </button>
                        <button
                          onClick={() => { setTimerLogSeed(null); setLoggingTime((v) => !v) }}
                          className="flex items-center gap-1 text-[10px] text-text-faint hover:text-accent transition-colors px-1.5 h-5 rounded border border-border hover:border-accent/40 active:scale-[0.97]"
                        >
                          <Plus size={9} />
                          {totalTimeLabel ? 'Log' : 'Add'}
                        </button>
                      </>
                    )}
                  </div>
                </DetailRow>

                {/* Individual time entries */}
                <AnimatePresence initial={false}>
                  {timeEntries.map((entry) => (
                    <motion.div
                      key={entry.id}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.18, ease: [0.23, 1, 0.32, 1] }}
                      className="overflow-hidden"
                    >
                      <TimeEntry entry={entry} onDelete={() => deleteEntry(entry.id)} />
                    </motion.div>
                  ))}
                </AnimatePresence>

                <AnimatePresence>
                  {loggingTime && (
                    <div className="py-2">
                      <LogTimeForm
                        onSubmit={logTime}
                        onCancel={() => { setLoggingTime(false); setTimerLogSeed(null) }}
                        initialHours={timerLogSeed?.hours ?? ''}
                        initialMins={timerLogSeed?.mins ?? ''}
                      />
                    </div>
                  )}
                </AnimatePresence>

                {/* Recurrence */}
                <DetailRow icon={RotateCcw} label="Repeat">
                  <div className="flex items-center gap-2 flex-wrap">
                    <RecurrenceSelector
                      value={task.recurrence}
                      onChange={(val) => updateTask(task.id, { recurrence: val })}
                    />
                    {task.recurrence?.freq && !task.completed && (
                      task.recurrence.nextSpawned ? (
                        <span className="text-[11px] text-text-faint italic">· next scheduled</span>
                      ) : (
                        <button
                          onClick={() => spawnNextRecurrence(task.id)}
                          className="text-[11px] text-accent/70 hover:text-accent transition-colors"
                        >
                          · schedule next
                        </button>
                      )
                    )}
                  </div>
                </DetailRow>

                {/* Tags */}
                <DetailRow icon={Tag} label="Tags">
                  <div className="flex flex-wrap gap-1 items-center">
                    {tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-2 h-5 rounded-md bg-surface-raised border border-border text-[10px] font-medium text-text-muted"
                      >
                        {tag}
                        <button onClick={() => removeTag(tag)} className="hover:text-text leading-none ml-0.5">×</button>
                      </span>
                    ))}
                    {addingTag ? (
                      <input
                        autoFocus
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') addTag(); if (e.key === 'Escape') { setNewTag(''); setAddingTag(false) } }}
                        onBlur={addTag}
                        placeholder="tag name"
                        className="text-xs bg-surface-raised border border-accent/40 rounded-md px-2 h-5 w-20 outline-none text-text"
                      />
                    ) : (
                      <button
                        onClick={() => setAddingTag(true)}
                        className="text-text-faint hover:text-text-muted transition-colors"
                      >
                        <Plus size={12} />
                      </button>
                    )}
                  </div>
                </DetailRow>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Description */}
        <div>
          <p className="text-[10px] text-text-faint mb-2 font-semibold uppercase tracking-wider">Description</p>
          {editingDesc || !localDesc ? (
            /* Edit mode */
            <textarea
              ref={descRef}
              value={localDesc}
              onChange={handleDescriptionChange}
              onFocus={() => setEditingDesc(true)}
              onBlur={(e) => {
                setEditingDesc(false)
                if (descDirty.current) { clearTimeout(descTimer.current); descDirty.current = false; updateTask(task.id, { description: localDesc }) }
                setTimeout(() => setMentionOpen(false), 150)
              }}
              onKeyDown={(e) => {
                if (!mentionOpen || mentionTargetRef.current !== 'description') return
                if (mentionSuggestions.length === 0) return
                if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex((i) => (i + 1) % mentionSuggestions.length) }
                else if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIndex((i) => (i - 1 + mentionSuggestions.length) % mentionSuggestions.length) }
                else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(mentionSuggestions[mentionIndex]) }
                else if (e.key === 'Escape') { setMentionOpen(false) }
              }}
              placeholder="Add a description… (type @ to mention)"
              rows={1}
              className="w-full bg-surface-raised border border-border rounded-lg px-3 py-2.5 text-sm text-text placeholder:text-text-faint resize-none focus:outline-none focus:border-accent/40 transition-colors overflow-hidden min-h-[72px]"
            />
          ) : (
            /* Read mode — links are clickable, click anywhere else to edit */
            <div
              onMouseDown={(e) => {
                // Capture click offset in the text nodes before we switch to textarea
                let offset = localDesc.length
                try {
                  let range
                  if (document.caretRangeFromPoint) {
                    range = document.caretRangeFromPoint(e.clientX, e.clientY)
                  } else if (document.caretPositionFromPoint) {
                    const pos = document.caretPositionFromPoint(e.clientX, e.clientY)
                    if (pos) { range = document.createRange(); range.setStart(pos.offsetNode, pos.offset) }
                  }
                  if (range) {
                    const walker = document.createTreeWalker(e.currentTarget, NodeFilter.SHOW_TEXT)
                    let acc = 0, node
                    while ((node = walker.nextNode())) {
                      if (node === range.startContainer) { offset = acc + range.startOffset; break }
                      acc += node.textContent.length
                    }
                  }
                } catch {}
                descClickOffset.current = offset
              }}
              onClick={(e) => {
                if (e.target.tagName === 'A') return
                const offset = descClickOffset.current ?? localDesc.length
                setEditingDesc(true)
                setTimeout(() => {
                  if (descRef.current) {
                    descRef.current.focus()
                    const clamped = Math.min(offset, descRef.current.value.length)
                    descRef.current.setSelectionRange(clamped, clamped)
                  }
                }, 0)
              }}
              className="w-full bg-surface-raised border border-border rounded-lg px-3 py-2.5 text-sm text-text min-h-[72px] cursor-text whitespace-pre-wrap leading-relaxed"
            >
              {renderCommentText(localDesc, members)}
            </div>
          )}
          {localDesc.length > 0 && (
            <p className="text-[10px] text-text-faint text-right mt-0.5">{localDesc.length} chars</p>
          )}
        </div>

        {/* Inspo */}
        <div
          onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); if (e.dataTransfer.types.includes('Files')) setDraggingInspo(true) }}
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (e.dataTransfer.types.includes('Files')) setDraggingInspo(true) }}
          onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDraggingInspo(false) }}
          onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setDraggingInspo(false); handleInspoFiles(e.dataTransfer.files) }}
          className={cn('rounded-xl transition-colors', draggingInspo && 'ring-1 ring-accent/40 bg-accent/5')}
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] text-text-faint font-semibold uppercase tracking-wider">Inspo</p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => inspoFileRef.current?.click()}
                className="flex items-center gap-1 text-[10px] text-text-faint hover:text-text-muted transition-colors active:scale-[0.97]"
              >
                <Upload size={10} />
                Upload
              </button>
              <button
                onClick={() => setAddingInspo((v) => !v)}
                className="flex items-center gap-1 text-[10px] text-text-faint hover:text-text-muted transition-colors active:scale-[0.97]"
              >
                <Link2 size={10} />
                Link
              </button>
            </div>
            <input
              ref={inspoFileRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => handleInspoFiles(e.target.files)}
            />
          </div>

          {/* URL input */}
          <AnimatePresence>
            {addingInspo && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.15, ease: [0.23, 1, 0.32, 1] }}
                className="overflow-hidden mb-2"
              >
                <div className="flex gap-2">
                  <input
                    autoFocus
                    value={inspoUrl}
                    onChange={(e) => setInspoUrl(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') addInspoLink(inspoUrl)
                      if (e.key === 'Escape') { setAddingInspo(false); setInspoUrl('') }
                    }}
                    placeholder="Paste a URL…"
                    className="flex-1 bg-surface-raised border border-border rounded-lg px-3 h-8 text-sm text-text placeholder:text-text-faint outline-none focus:border-accent/40 transition-colors"
                  />
                  <button
                    onClick={() => addInspoLink(inspoUrl)}
                    disabled={!inspoUrl.trim()}
                    className="px-3 h-8 rounded-lg bg-accent text-black text-xs font-semibold hover:opacity-90 transition-opacity active:scale-[0.97] disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Add
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Inspo cards */}
          {draggingInspo && attachments.filter((a) => a.type === 'inspo').length > 0 && (
            <div className="border border-dashed border-accent/50 rounded-xl py-2 mb-2 text-xs text-accent flex items-center justify-center gap-1.5">
              <Upload size={11} />
              Drop to add inspo
            </div>
          )}
          {attachments.filter((a) => a.type === 'inspo').length > 0 ? (
            <div className="space-y-2">
              {attachments.filter((a) => a.type === 'inspo').map((inspo) => {
                // Uploaded image — render identical to regular attachment image card with lightbox
                if (inspo.isFile && inspo.isImage && inspo.url) {
                  const imgIdx = imageAttachments.findIndex((a) => a.id === inspo.id)
                  return (
                    <div key={inspo.id} className="group relative rounded-xl overflow-hidden border border-border bg-surface-raised" style={{ aspectRatio: '3/2' }}>
                      <img src={inspo.image || inspo.url} alt={inspo.name || ''} className="w-full h-full object-cover" />
                      <button
                        onClick={() => imgIdx !== -1 && setLightboxIdx(imgIdx)}
                        aria-label="Expand image"
                        className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-150 bg-black/20"
                      >
                        <div className="bg-black/60 backdrop-blur-sm rounded-full p-2.5 transition-transform duration-100 active:scale-[0.92]">
                          <Maximize2 size={15} className="text-white" aria-hidden="true" />
                        </div>
                      </button>
                      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                        <button onClick={(e) => { e.stopPropagation(); downloadFile(inspo.url, inspo.name) }} aria-label="Download" className="bg-black/60 backdrop-blur-sm text-white rounded-full p-1.5 hover:bg-black/80 transition-colors">
                          <Download size={11} aria-hidden="true" />
                        </button>
                        <button onClick={() => removeAttachment(inspo.id)} aria-label="Remove" className="bg-black/60 backdrop-blur-sm text-white rounded-full p-1.5 hover:bg-red-500/80 transition-colors">
                          <X size={11} aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  )
                }
                // Uploading placeholder
                if (inspo.loading) {
                  return (
                    <div key={inspo.id} className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-border bg-surface-raised">
                      <Loader2 size={12} className="text-text-faint animate-spin flex-shrink-0" />
                      <span className="text-xs text-text-faint truncate">{inspo.name || inspo.url}</span>
                    </div>
                  )
                }
                // URL card or non-image file — link card with optional OG preview
                return (
                  <div key={inspo.id} className="group relative rounded-xl border border-border overflow-hidden bg-surface-raised">
                    <a href={inspo.url} target="_blank" rel="noopener noreferrer" className="block">
                      {(inspo.isFile ? false : inspo.image) && (
                        <div className="w-full h-28 overflow-hidden">
                          <img src={inspo.image} alt={inspo.title || ''} className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="flex items-center gap-2 px-3 py-2 pr-8">
                        {inspo.isFile ? (
                          <FileTypeIcon name={inspo.name} size={11} />
                        ) : inspo.favicon ? (
                          <img src={inspo.favicon} alt="" className="w-3.5 h-3.5 rounded flex-shrink-0" />
                        ) : (
                          <Link2 size={11} className="text-text-faint flex-shrink-0" />
                        )}
                        <span className="text-xs text-text truncate flex-1">{inspo.name || inspo.title || inspo.url}</span>
                        <ArrowUpRight size={11} className="text-text-faint flex-shrink-0" />
                      </div>
                    </a>
                    <button
                      onClick={() => removeAttachment(inspo.id)}
                      className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 bg-black/60 backdrop-blur-sm text-white rounded-full p-1 hover:bg-red-500/80 transition-all"
                    >
                      <X size={10} />
                    </button>
                  </div>
                )
              })}
            </div>
          ) : (
            !addingInspo && (
              draggingInspo ? (
                <div className="border border-dashed border-accent/50 rounded-xl py-4 text-xs text-accent flex items-center justify-center gap-1.5">
                  <Upload size={11} />
                  Drop to add inspo
                </div>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={() => setAddingInspo(true)}
                    className="flex-1 border border-dashed border-border rounded-xl py-3 text-xs text-text-faint hover:text-text-muted hover:border-border/80 transition-colors flex items-center justify-center gap-1.5"
                  >
                    <Link2 size={11} />
                    Add link
                  </button>
                  <button
                    onClick={() => inspoFileRef.current?.click()}
                    className="flex-1 border border-dashed border-border rounded-xl py-3 text-xs text-text-faint hover:text-text-muted hover:border-border/80 transition-colors flex items-center justify-center gap-1.5"
                  >
                    <Upload size={11} />
                    Upload file
                  </button>
                </div>
              )
            )
          )}
        </div>

        {/* Subtasks */}
        <div>
          <p className="text-[10px] text-text-faint mb-2 font-semibold uppercase tracking-wider">
            Subtasks {childTasks.length > 0 && `(${childTasks.filter((s) => s.completed).length}/${childTasks.length})`}
          </p>
          <div className="rounded-xl border border-border overflow-hidden">
            {childTasks.map((child) => (
              <div key={child.id} className="border-b border-border last:border-b-0">
                <SubtaskRow
                  child={child}
                  grandchildren={tasks.filter((t) => t.parentId === child.id).length}
                  onOpen={() => setSelectedTask(child.id)}
                  onToggle={() => toggleTask(child.id)}
                  onDelete={() => deleteTask(child.id)}
                  onDateChange={(val) => updateTask(child.id, { dueDate: val })}
                  openCtx={openCtx}
                />
              </div>
            ))}
            <div className="flex items-center gap-2 px-3 py-2.5">
              <Plus size={12} className="text-text-faint flex-shrink-0" />
              <input
                value={newSubtask}
                onChange={(e) => setNewSubtask(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addSubtask()}
                placeholder="Add subtask…"
                className="flex-1 bg-transparent text-sm text-text placeholder:text-text-faint focus:outline-none"
              />
              {newSubtask && (
                <button onClick={addSubtask} className="text-accent text-xs font-medium active:scale-[0.97] flex-shrink-0">Add</button>
              )}
            </div>
          </div>
        </div>

        {/* Attachments */}
        <div>
          <p className="text-[10px] text-text-faint mb-2 font-semibold uppercase tracking-wider">Attachments</p>
          <div className="space-y-2">
            {attachments.filter((a) => a.type !== 'inspo' && (a.isImage || a.isVideo || isPdf(a))).length > 0 && (
              <div className="flex flex-col gap-2 mb-1">
                {attachments.filter((a) => a.type !== 'inspo' && (a.isImage || a.isVideo || isPdf(a))).map((att) => {
                  const imgIdx = imageAttachments.findIndex((a) => a.id === att.id)
                  const isThisPdf = isPdf(att)
                  const isThisVideo = att.isVideo
                  return (
                    <div
                      key={att.id}
                      onClick={() => !att.uploading && !isThisPdf && setLightboxIdx(imgIdx)}
                      className="relative group rounded-xl overflow-hidden border border-border bg-surface-raised"
                      style={{ aspectRatio: '3/2', cursor: (!att.uploading && !isThisPdf) ? 'pointer' : 'default' }}
                    >
                      {att.uploading ? (
                        <div className="w-full h-full flex flex-col items-center justify-center gap-2 px-5">
                          <Loader2 size={16} className="text-text-faint animate-spin flex-shrink-0" />
                          {att.progress > 0 && (
                            <div className="w-full">
                              <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                                <div className="h-full bg-accent rounded-full transition-all duration-300" style={{ width: `${att.progress}%` }} />
                              </div>
                              <p className="text-[10px] text-text-faint mt-1 text-center">{att.progress}%</p>
                            </div>
                          )}
                        </div>
                      ) : isThisPdf ? (
                        <PdfThumbnail url={att.url} />
                      ) : (
                        <AttachmentMedia
                          attachment={att}
                          taskId={task.id}
                          className="w-full h-full"
                          alt={att.name}
                        />
                      )}
                      {!att.uploading && (
                        <>
                          {/* Hover overlay — expand for photos, play for videos */}
                          {!isThisPdf && (
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-150 bg-black/20 pointer-events-none">
                              <div className="bg-black/60 backdrop-blur-sm rounded-full p-2.5">
                                {isThisVideo
                                  ? <Play size={15} className="text-white ml-0.5" fill="currentColor" aria-hidden="true" />
                                  : <Maximize2 size={15} className="text-white" aria-hidden="true" />
                                }
                              </div>
                            </div>
                          )}
                          {/* Download + delete — top right */}
                          <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                            <button
                              onClick={(e) => { e.stopPropagation(); downloadFile(att.url, att.name) }}
                              aria-label="Download"
                              className="bg-black/60 backdrop-blur-sm text-white rounded-full p-1.5 hover:bg-black/80 transition-colors"
                            >
                              <Download size={11} aria-hidden="true" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); removeAttachment(att.id) }}
                              aria-label="Remove"
                              className="bg-black/60 backdrop-blur-sm text-white rounded-full p-1.5 hover:bg-red-500/80 transition-colors"
                            >
                              <X size={11} aria-hidden="true" />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
            {attachments.filter((a) => a.type !== 'inspo' && !a.isImage && !a.isVideo && !isPdf(a)).map((att) => (
              <div key={att.id} className="flex items-center gap-2 group">
                {att.uploading ? (
                  <Loader2 size={12} className="text-text-faint animate-spin flex-shrink-0" />
                ) : (
                  <FileTypeIcon name={att.name} size={12} />
                )}
                {att.uploading ? (
                  <span className="text-sm text-text-faint flex-1 truncate">Uploading {att.name}…</span>
                ) : att.url ? (
                  <a href={att.url} target="_blank" rel="noreferrer" className="text-sm text-accent hover:underline flex-1 truncate">{att.name}</a>
                ) : (
                  <span className="text-sm text-text-muted flex-1 truncate">{att.name}</span>
                )}
                {!att.uploading && (
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                    {att.url && (
                      <button onClick={(e) => { e.stopPropagation(); downloadFile(att.url, att.name) }} className="text-text-faint hover:text-accent">
                        <Download size={11} />
                      </button>
                    )}
                    <button onClick={() => removeAttachment(att.id)} className="text-text-faint hover:text-red-400">
                      <X size={11} />
                    </button>
                  </div>
                )}
              </div>
            ))}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-1.5 text-sm text-text-faint hover:text-text-muted transition-colors"
            >
              <Paperclip size={12} />
              Add attachment
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*,.pdf,.doc,.docx,.txt,.svg"
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />
          </div>
        </div>

        {/* Comment input */}
        <div className="relative">
          <p className="text-[10px] text-text-faint mb-2.5 font-semibold uppercase tracking-wider">Comment</p>
          <div className="flex gap-3 items-start">
            <Avatar user={currentUser} size="sm" className="mt-1.5 flex-shrink-0" />
            <div className="flex-1 flex items-end gap-2 bg-surface-raised border border-border rounded-xl px-3.5 py-2.5 focus-within:border-accent/40 transition-colors">
              <textarea
                ref={commentRef}
                value={newComment}
                onChange={handleCommentChange}
                onKeyDown={handleCommentKeyDown}
                onBlur={() => setTimeout(() => setMentionOpen(false), 150)}
                placeholder="Comment or @ mention…"
                rows={2}
                className="flex-1 bg-transparent text-sm text-text placeholder:text-text-faint resize-none focus:outline-none leading-relaxed"
                style={{ minHeight: '40px' }}
              />
              {newComment.trim() && (
                <button
                  onClick={addComment}
                  className="text-accent hover:opacity-80 transition-opacity flex-shrink-0 active:scale-[0.94]"
                >
                  <Send size={15} />
                </button>
              )}
            </div>
          </div>
          {newComment && <p className="text-xs text-text-faint mt-1 ml-11">↵ Enter to send · Shift+Enter for newline · @ to mention</p>}

          {/* @ mention popover — portaled to escape backdrop-filter stacking context */}
          {createPortal(
            <AnimatePresence>
              {mentionOpen && mentionSuggestions.length > 0 && (
                <motion.div
                  data-floating-ui
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.12, ease: [0.23, 1, 0.32, 1] }}
                  style={{ position: 'fixed', top: mentionPos.top, left: mentionPos.left, transform: mentionPos.flipUp ? 'translateY(-100%)' : 'none' }}
                  className="bg-surface border border-border rounded-xl shadow-xl overflow-hidden z-[9000] min-w-[180px]"
                >
                  {mentionSuggestions.map((member, i) => (
                    <button
                      key={member.userId}
                      onMouseDown={(e) => { e.preventDefault(); insertMention(member) }}
                      className={cn(
                        'w-full flex items-center gap-2.5 px-3 h-8 text-sm text-left transition-colors',
                        i === mentionIndex ? 'bg-accent/10 text-text' : 'text-text-muted hover:bg-surface-raised'
                      )}
                    >
                      <Avatar user={{ name: member.name, avatar: member.avatar }} size="xs" />
                      <span className="text-sm">{member.name}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>,
            document.body
          )}
        </div>

        {/* Comments — always visible */}
        {sortedComments.length > 0 && (
          <div className="space-y-0.5">
            {sortedComments.map((entry) => (
              <div key={entry.id} className="flex gap-3 group py-2">
                <Avatar user={{ name: entry.author }} size="sm" className="mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0 bg-surface-raised rounded-xl px-3 py-2">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-sm font-semibold text-text">{entry.author}</span>
                    <span className="text-xs text-text-faint">
                      {new Date(entry.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                  {editingCommentId === entry.id ? (
                    <div className="flex flex-col gap-1.5">
                      <textarea
                        value={editingCommentText}
                        onChange={(e) => setEditingCommentText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveCommentEdit(entry.id) }
                          if (e.key === 'Escape') setEditingCommentId(null)
                        }}
                        autoFocus
                        rows={2}
                        className="w-full bg-surface border border-accent/40 rounded-lg px-2.5 py-1.5 text-sm text-text resize-none outline-none leading-relaxed"
                      />
                      <div className="flex gap-1.5">
                        <button onClick={() => saveCommentEdit(entry.id)} className="text-[11px] px-2 py-0.5 rounded bg-accent text-black font-semibold hover:opacity-90 active:scale-[0.97]">Save</button>
                        <button onClick={() => setEditingCommentId(null)} className="text-[11px] px-2 py-0.5 rounded border border-border text-text-faint hover:text-text active:scale-[0.97]">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-text leading-relaxed">{renderCommentText(entry.text, memberPool)}</p>
                  )}
                </div>
                <div className="opacity-0 group-hover:opacity-100 flex flex-col gap-1 flex-shrink-0 mt-2 transition-opacity">
                  <button
                    onClick={() => { setEditingCommentId(entry.id); setEditingCommentText(entry.text) }}
                    className="text-text-faint hover:text-accent transition-colors"
                    title="Edit"
                  >
                    <Pencil size={11} />
                  </button>
                  <button
                    onClick={() => deleteEntry(entry.id)}
                    className="text-text-faint hover:text-red-400 transition-colors"
                    title="Delete"
                  >
                    <X size={11} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Activity — collapsible, audit trail + time entries only */}
        {activityEntries.length > 0 && (
          <div>
            <button
              onClick={() => setActivityOpen((v) => !v)}
              className="flex items-center gap-1.5 text-[10px] font-semibold text-text-faint uppercase tracking-wider hover:text-text-muted transition-colors active:scale-[0.97] w-full"
            >
              <ChevronDown size={12} className={cn('transition-transform duration-200', activityOpen && 'rotate-180')} />
              Activity
              <span className="normal-case tracking-normal font-normal ml-0.5">· {activityEntries.length} update{activityEntries.length !== 1 ? 's' : ''}</span>
            </button>

            <AnimatePresence initial={false}>
              {activityOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                  className="overflow-hidden"
                >
                  <div className="space-y-0.5 mt-3">
                    {activityEntries.map((entry) => {
                      if (entry.type === 'activity') {
                        return (
                          <ActivityEntry
                            key={entry.id}
                            entry={entry}
                            sections={sections}
                            projects={projects}
                          />
                        )
                      }
                      if (entry.type === 'time') {
                        return (
                          <TimeEntry
                            key={entry.id}
                            entry={entry}
                            onDelete={() => deleteEntry(entry.id)}
                          />
                        )
                      }
                      return null
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </motion.div>

    {/* Image lightbox — portaled to document.body to escape backdrop-filter stacking context */}
    {createPortal(<AnimatePresence>
      {lightboxIdx !== null && imageAttachments[lightboxIdx] && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 bg-black/92 backdrop-blur-xl z-[600]"
            onClick={() => setLightboxIdx(null)}
            aria-hidden="true"
          />

          {/* Image / Video */}
          <motion.div
            key={lightboxIdx}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-[601] flex items-center justify-center p-10 pointer-events-none"
          >
            {imageAttachments[lightboxIdx].isVideo ? (
              <div className="relative pointer-events-auto" style={{ maxWidth: '90vw', maxHeight: '84vh' }}>
                <video
                  ref={lightboxVideoRef}
                  key={imageAttachments[lightboxIdx].url}
                  src={imageAttachments[lightboxIdx].url}
                  autoPlay
                  playsInline
                  className="rounded-xl shadow-2xl shadow-black/60 block"
                  style={{ maxWidth: '90vw', maxHeight: '84vh', width: '90vw', height: 'auto' }}
                  onLoadedData={(e) => { e.currentTarget.play().catch(() => {}) }}
                  onPlay={() => setLightboxVideoPaused(false)}
                  onPause={() => setLightboxVideoPaused(true)}
                  onError={(e) => {
                    const err = e.currentTarget.error
                    console.error('[video lightbox] error', 'code:', err?.code, 'msg:', err?.message)
                  }}
                  onClick={() => {
                    const v = lightboxVideoRef.current
                    if (!v) return
                    if (v.paused) { v.play() } else { v.pause() }
                    clearTimeout(lightboxIconTimerRef.current)
                    setLightboxIconVisible(true)
                    lightboxIconTimerRef.current = setTimeout(() => setLightboxIconVisible(false), 700)
                  }}
                />
                {/* Brief pause/play indicator */}
                <AnimatePresence>
                  {lightboxIconVisible && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.75 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 1.1 }}
                      transition={{ duration: 0.14, ease: [0.16, 1, 0.3, 1] }}
                      className="absolute inset-0 flex items-center justify-center pointer-events-none"
                    >
                      <div className="bg-black/55 backdrop-blur-sm rounded-full p-4">
                        {lightboxVideoPaused
                          ? <Play size={22} className="text-white ml-0.5" fill="currentColor" />
                          : <Pause size={22} className="text-white" fill="currentColor" />
                        }
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <img
                src={imageAttachments[lightboxIdx].url}
                alt={imageAttachments[lightboxIdx].name}
                className="pointer-events-auto rounded-xl shadow-2xl shadow-black/60 object-contain"
                style={{ maxWidth: '90vw', maxHeight: '84vh' }}
              />
            )}
          </motion.div>

          {/* Top-right controls */}
          <div className="fixed top-4 right-4 z-[602] flex gap-2">
            <button
              onClick={() => downloadFile(imageAttachments[lightboxIdx].url, imageAttachments[lightboxIdx].name)}
              aria-label="Download image"
              className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors active:scale-[0.94]"
              style={{ transition: 'background 150ms ease, transform 100ms ease-out' }}
            >
              <Download size={15} aria-hidden="true" />
            </button>
            <button
              onClick={() => setLightboxIdx(null)}
              aria-label="Close lightbox"
              className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors active:scale-[0.94]"
              style={{ transition: 'background 150ms ease, transform 100ms ease-out' }}
            >
              <X size={15} aria-hidden="true" />
            </button>
          </div>

          {/* Prev / next arrows */}
          {imageAttachments.length > 1 && (
            <>
              <button
                onClick={() => setLightboxIdx((i) => Math.max(i - 1, 0))}
                disabled={lightboxIdx === 0}
                aria-label="Previous image"
                className="fixed left-4 top-1/2 -translate-y-1/2 z-[602] w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors active:scale-[0.94] disabled:opacity-20 disabled:pointer-events-none"
                style={{ transition: 'background 150ms ease, transform 100ms ease-out' }}
              >
                <ChevronLeft size={18} aria-hidden="true" />
              </button>
              <button
                onClick={() => setLightboxIdx((i) => Math.min(i + 1, imageAttachments.length - 1))}
                disabled={lightboxIdx === imageAttachments.length - 1}
                aria-label="Next image"
                className="fixed right-4 top-1/2 -translate-y-1/2 z-[602] w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors active:scale-[0.94] disabled:opacity-20 disabled:pointer-events-none"
                style={{ transition: 'background 150ms ease, transform 100ms ease-out' }}
              >
                <ChevronRight size={18} aria-hidden="true" />
              </button>

              {/* Dot indicators */}
              <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[602] flex gap-1.5">
                {imageAttachments.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setLightboxIdx(i)}
                    aria-label={`Go to image ${i + 1}`}
                    className="rounded-full transition-all duration-150 active:scale-[0.85]"
                    style={{
                      width: i === lightboxIdx ? 20 : 6,
                      height: 6,
                      background: i === lightboxIdx ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.3)',
                    }}
                  />
                ))}
              </div>
            </>
          )}

          {/* File name */}
          <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[602] pointer-events-none" style={{ marginBottom: imageAttachments.length > 1 ? 24 : 0 }}>
            <p className="text-[11px] text-white/50 text-center">{imageAttachments[lightboxIdx].name}</p>
          </div>
        </>
      )}
    </AnimatePresence>, document.body)}
    </>
  )
}
